import { RiskBadge, StatusBadge } from '@/components/ui/Badge'
import { LatencyBar } from '@/components/ui/LatencyBar'
import { formatClock, formatRelative, shortRepo } from '@/lib/utils/format'
import type { ExecutionRow } from '@/types/domain'

/**
 * Execution history.
 *
 * The latency column is a bar rather than a number alone, drawn against the slowest call
 * on the page so rows are comparable to each other. Its two segments separate our time
 * from GitHub's, which is the first question anyone asks about a slow call.
 */
export function ExecutionTable({
  rows,
  selectedRequestId,
  onSelect,
}: {
  rows: ExecutionRow[]
  selectedRequestId?: string | null
  onSelect: (row: ExecutionRow) => void
}) {
  const scaleMs = Math.max(...rows.map((row) => row.latency_ms), 1)

  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col" className="w-20">
              Time
            </th>
            <th scope="col">Tool</th>
            <th scope="col" className="w-24">
              Risk
            </th>
            <th scope="col" className="w-28">
              Status
            </th>
            <th scope="col" className="w-40">
              Repository
            </th>
            <th scope="col" className="w-56">
              Latency
            </th>
            <th scope="col" className="w-44">
              Error
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              data-clickable="true"
              data-selected={row.request_id === selectedRequestId}
              onClick={() => onSelect(row)}
              className={row.request_id === selectedRequestId ? 'bg-raised' : undefined}
            >
              <td className="mono text-ink-muted" title={formatRelative(row.created_at)}>
                {formatClock(row.created_at)}
              </td>
              <td className="mono max-w-[22rem] truncate text-ink">{row.tool_name}</td>
              <td>
                <RiskBadge risk={row.operation_type as 'READ' | 'WRITE' | 'DESTRUCTIVE' | 'META'} />
              </td>
              <td>
                <StatusBadge status={row.status} />
              </td>
              <td className="mono truncate text-ink-dim" title={row.repository ?? undefined}>
                {row.repository ? shortRepo(row.repository) : '—'}
              </td>
              <td>
                <LatencyBar
                  totalMs={row.latency_ms}
                  githubMs={row.github_latency_ms}
                  scaleMs={scaleMs}
                />
              </td>
              <td className="mono truncate text-ink-muted">
                {row.error_type ? (
                  <span className="text-fail">
                    {row.error_type}
                    {row.external_status_code ? ` ${row.external_status_code}` : ''}
                  </span>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
